import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { authService, AuthResponse, LoginResponse } from '../services/authService';
import { IDLE_TIMEOUT_MS, LAST_ACTIVITY_KEY } from '../config/session';

interface User {
  nombre: string;
  email: string;
  userType: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<LoginResponse>;
  verify2FA: (tempToken: string, code: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const isTokenValid = (token: string): boolean => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return typeof payload.exp === 'number' && payload.exp * 1000 > Date.now();
    } catch {
      return false;
    }
  };

  const isIdleExpired = (): boolean => {
    const lastActivity = localStorage.getItem(LAST_ACTIVITY_KEY);
    if (!lastActivity) return true;
    return Date.now() - Number(lastActivity) > IDLE_TIMEOUT_MS;
  };

  const checkAuth = useCallback(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    if (token && storedUser && isTokenValid(token) && !isIdleExpired()) {
      try {
        const parsed = JSON.parse(storedUser);
        // migrate old sessions without userType
        if (!parsed.userType) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem(LAST_ACTIVITY_KEY);
          setLoading(false);
          return;
        }
        setUser(parsed);
      } catch {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem(LAST_ACTIVITY_KEY);
      }
    } else if (token || storedUser) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem(LAST_ACTIVITY_KEY);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email: string, password: string) => {
    const response = await authService.login(email, password);
    return response;
  };

  const verify2FA = async (tempToken: string, code: string) => {
    const response: AuthResponse = await authService.verify2FA(tempToken, code);
    localStorage.setItem('token', response.token);
    localStorage.setItem('user', JSON.stringify({ nombre: response.nombre, email: response.email, userType: response.userType }));
    localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
    setUser({ nombre: response.nombre, email: response.email, userType: response.userType });
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem(LAST_ACTIVITY_KEY);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{
      isAuthenticated: !!user,
      user,
      loading,
      login,
      verify2FA,
      logout,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
